/**
 * useBackDismiss — make the Android/browser back gesture close in-app overlays
 * (and step out of 3D) instead of exiting the app.
 *
 * This app ships as an installed Android TWA. With no history entries pushed,
 * the system back button/gesture ejects the user from the app the instant they
 * try to dismiss a modal, dropdown, watch-zone editor, fire-detail sheet, or
 * leave 3D — exactly when, under stress, they're reaching for "close this and
 * get back to the map". See docs/mobile-ux-uplift-plan.md §1.3.
 *
 * Model: a module-level LIFO stack of open dismissable layers. Each open layer
 * pushes one history entry and registers its close callback. A single popstate
 * listener closes the topmost layer. Closing a layer by other means (its own ✕,
 * Escape, unmount) rolls back the history entry it added, suppressing the
 * resulting popstate so the layer beneath isn't closed too.
 *
 * EXIT GUARD (installBackExitGuard): in an installed TWA there is otherwise
 * nothing between "base map, nothing open" and exiting the app, so a single
 * stray back closes the whole app. The guard keeps one sentinel history entry
 * so the first root-level back is caught and turned into a "Press back again to
 * exit" toast; a second back within a short window actually exits. This is the
 * standard Android double-back-to-exit pattern (Material discourages a modal
 * "are you sure?" dialog on back).
 *
 * NOTE: back-gesture behaviour is hard to unit-test; verify on a real Android
 * device / emulator.
 */

import { useEffect, useRef } from 'react';
import { toast } from './useToast';

interface Entry {
  id: string;
  close: () => void;
}

const stack: Entry[] = [];
let listenerInstalled = false;
// True while we are programmatically calling history.back() to undo our own
// pushed entry — tells the popstate handler to ignore that one event.
let suppressNextPop = false;

// Exit-guard state (only armed in a standalone/TWA context).
let exitGuardActive = false;
let sentinelInstalled = false;
let readyToExitUntil = 0;
const EXIT_WINDOW_MS = 2500;

function pushSentinel() {
  window.history.pushState({ __backExitSentinel: true }, '');
}

function handlePopState() {
  if (suppressNextPop) {
    suppressNextPop = false;
    return;
  }
  const top = stack.pop();
  if (top) {
    top.close();
    return;
  }
  // Stack empty → a root-level back. If the exit guard is armed, apply the
  // double-back-to-exit pattern instead of letting the app close immediately.
  if (exitGuardActive) {
    const now = Date.now();
    if (now < readyToExitUntil) {
      // Second back within the window → actually let the app exit. History has
      // already popped the sentinel; go back once more to unwind past our base.
      window.history.back();
      return;
    }
    // First back at root → re-arm: refill the sentinel and prompt.
    readyToExitUntil = now + EXIT_WINDOW_MS;
    pushSentinel();
    toast.show({
      type: 'info',
      title: '',
      message: 'Press back again to exit',
      duration: EXIT_WINDOW_MS,
    });
  }
}

function ensureListener() {
  if (listenerInstalled || typeof window === 'undefined') return;
  window.addEventListener('popstate', handlePopState);
  listenerInstalled = true;
}

/**
 * Arm the double-back-to-exit guard. Call once, only in an installed
 * PWA/TWA (standalone) context — in a normal browser tab we must NOT trap the
 * back button (it should navigate away as usual).
 */
export function installBackExitGuard(): void {
  if (typeof window === 'undefined' || sentinelInstalled) return;
  ensureListener();
  pushSentinel();
  sentinelInstalled = true;
  exitGuardActive = true;
}

/** Whether we're running as an installed PWA/TWA (standalone display). */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    // iOS Safari standalone flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

/**
 * @param isOpen  whether the layer is currently open
 * @param close   closes the layer (kept in a ref, so it need not be memoised)
 * @param id      stable identifier for this layer, used for history state
 */
export function useBackDismiss(isOpen: boolean, close: () => void, id: string): void {
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;

    ensureListener();
    const entry: Entry = { id, close: () => closeRef.current() };
    stack.push(entry);
    window.history.pushState({ __backDismiss: id }, '');

    return () => {
      const idx = stack.lastIndexOf(entry);
      if (idx === -1) {
        // Already removed by handlePopState (user pressed back): the history
        // entry is gone too, nothing to roll back.
        return;
      }
      stack.splice(idx, 1);
      // Closed programmatically (button / Escape / unmount). If our pushed
      // entry is still the current history state, pop it so we don't leak a
      // phantom entry, and suppress the popstate it triggers.
      const state = window.history.state as { __backDismiss?: string } | null;
      if (state && state.__backDismiss === id) {
        suppressNextPop = true;
        window.history.back();
      }
    };
  }, [isOpen, id]);
}
